import type { BookEventUser, EagreementsSnapshot, PaymentMethodOption } from './bookEvent.types';

export const PAYMENT_METHODS = {
  PAY_LATER: 'pay_later',
  CREDITS: 'credits',
  CARD: 'card',
  DIRECT_DEBIT: 'direct_debit',
} as const;

export type BookingGateKind =
  | 'needsTerms'
  | 'notEligible'
  | 'anonymous'
  | 'membershipExpired'
  | 'needsCard'
  | 'inFlight'
  | 'blockedByEagreements'
  | 'ok';

export type BookingGateInput = {
  mode?: 'book' | 'purchase';
  eagreementsOnly?: boolean;
  user?: BookEventUser | null;
  event?: unknown;
  payment?: { selected?: PaymentMethodOption | null } | PaymentMethodOption[] | null;
  priceToPay?: number | null;
  creditsToPay?: number;
  termsAccepted?: boolean;
  isFirstBook?: boolean;
  transactionInProgress?: boolean;
  isFtcEnabled?: boolean;
  eagreements?: Pick<EagreementsSnapshot, 'flagResolved' | 'mandatoryEnabled' | 'documentsSigned'>;
  formatExpiryLocal?: (timestamp: number) => string;
  getBranchLocalTime?: () => string;
};

function isEmpty(value: unknown): boolean {
  if (value == null) {
    return true;
  }
  if (typeof value === 'string' || Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === 'object') {
    return Object.keys(value as object).length === 0;
  }
  return true;
}

function isMembershipSubscription(user: BookEventUser | null | undefined): boolean {
  return !isEmpty(user) && !isEmpty(user?.membership) && !isEmpty(user?.membership?.subscription);
}

function isExpiryBeforeLocalTime(
  expiryTimestamp: number | undefined,
  localTime: string,
  formatExpiryLocal: (timestamp: number) => string
): boolean {
  if (expiryTimestamp == null) {
    return false;
  }
  const membershipExpiryDate = formatExpiryLocal(expiryTimestamp);
  const endOfDay = `${membershipExpiryDate.slice(0, 10)}T23:59:59`;
  return endOfDay < localTime;
}

function isSubscriptionExpired(
  subscription: NonNullable<NonNullable<BookEventUser['membership']>['subscription']>,
  localTime: string,
  formatExpiryLocal: (timestamp: number) => string
): boolean {
  if (!subscription.end_date || subscription.auto_renewal) {
    return false;
  }
  return isExpiryBeforeLocalTime(subscription.end_date, localTime, formatExpiryLocal);
}

export function isMembershipExpired(
  user: BookEventUser | null | undefined,
  formatExpiryLocal: (timestamp: number) => string,
  getBranchLocalTime: () => string
): boolean {
  if (isEmpty(user) || isEmpty(user?.membership)) {
    return false;
  }

  const localTime = getBranchLocalTime();
  if (isMembershipSubscription(user)) {
    return isSubscriptionExpired(user!.membership!.subscription!, localTime, formatExpiryLocal);
  }
  return isExpiryBeforeLocalTime(user?.membership?.expiry_date, localTime, formatExpiryLocal);
}

function isEagreementsBlocking(
  eagreements: BookingGateInput['eagreements'] | undefined
): boolean {
  if (!eagreements) {
    return false;
  }
  if (!eagreements.flagResolved) {
    return true;
  }
  return !!eagreements.mandatoryEnabled && !eagreements.documentsSigned;
}

function selectedPaymentKey(payment: BookingGateInput['payment']): string {
  if (!payment || Array.isArray(payment)) {
    return '';
  }
  return payment.selected?.key || '';
}

function needsCard(input: BookingGateInput): boolean {
  const hasToPay = input.priceToPay !== null && (input.priceToPay ?? 0) > 0;
  if (!hasToPay) {
    return false;
  }
  const willPayWithCard = selectedPaymentKey(input.payment).toLowerCase() === PAYMENT_METHODS.CARD;
  return willPayWithCard && isEmpty(input.user?.card);
}

function isInFlight(input: BookingGateInput): boolean {
  return !input.event || !input.payment || !!input.transactionInProgress;
}

type GateBlocker = Exclude<BookingGateKind, 'ok'> | null;

function termsBlocker(input: BookingGateInput): GateBlocker {
  return input.isFirstBook && !input.termsAccepted ? 'needsTerms' : null;
}

function eligibilityBlocker(input: BookingGateInput): GateBlocker {
  const userHasCredits = (input.creditsToPay || 0) > 0;
  return input.priceToPay === null && !userHasCredits ? 'notEligible' : null;
}

function anonymousBlocker(input: BookingGateInput): GateBlocker {
  return isEmpty(input.user) ? 'anonymous' : null;
}

function membershipBlocker(input: BookingGateInput): GateBlocker {
  const formatExpiryLocal = input.formatExpiryLocal || (() => '');
  const getBranchLocalTime = input.getBranchLocalTime || (() => '');
  return isMembershipExpired(input.user, formatExpiryLocal, getBranchLocalTime)
    ? 'membershipExpired'
    : null;
}

const BOOKING_BLOCKERS: Array<(input: BookingGateInput) => GateBlocker> = [
  termsBlocker,
  eligibilityBlocker,
  anonymousBlocker,
  membershipBlocker,
  (input) => (needsCard(input) ? 'needsCard' : null),
  (input) => (isInFlight(input) ? 'inFlight' : null),
  (input) => (isEagreementsBlocking(input.eagreements) ? 'blockedByEagreements' : null),
];

function evaluateBook(input: BookingGateInput): { kind: BookingGateKind } {
  for (const evaluate of BOOKING_BLOCKERS) {
    const blocker = evaluate(input);
    if (blocker) {
      return { kind: blocker };
    }
  }
  return { kind: 'ok' };
}

export function evaluateBookingGate(input: BookingGateInput = {}): { kind: BookingGateKind } {
  if (input.eagreementsOnly) {
    return {
      kind: isEagreementsBlocking(input.eagreements) ? 'blockedByEagreements' : 'ok',
    };
  }

  if ((input.mode || 'book') === 'purchase') {
    return { kind: input.isFtcEnabled && !input.termsAccepted ? 'needsTerms' : 'ok' };
  }

  return evaluateBook(input);
}

export function isEmptyValue(value: unknown): boolean {
  return isEmpty(value);
}
